import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import BackButton from '../components/BackButton';
import AdBanner from '../components/AdBanner';
import './styles/ContentPages.css';

const StudyTipsPage = () => {
  const { i18n } = useTranslation();
  const isRu = i18n.language === 'ru';

  return (
    <div className="content-page">
      <Helmet>
        <title>{isRu ? 'Как подготовиться к тесту YKI: 7 проверенных советов | YKI Testi' : 'How to Study for the YKI Test: 7 Proven Tips | YKI Testi'}</title>
        <meta name="description" content={isRu ? '7 проверенных стратегий для сдачи теста YKI по финскому языку. Советы по лексике, грамматике, аудированию, письму, говорению и подготовке к экзамену.' : '7 proven study strategies to pass the YKI Finnish language proficiency test. Tips on vocabulary, grammar, listening, writing, speaking and test-day preparation.'} />
        <link rel="canonical" href="https://ykitesti.fi/study-tips" />
      </Helmet>
      <BackButton to="/" label={isRu ? 'Главная' : 'Home'} />
      <h1 className="content-page-title">{isRu ? 'Советы по подготовке к тесту YKI' : 'Study Tips for the YKI Test'}</h1>
      <p className="content-page-subtitle">{isRu ? 'Проверенные стратегии, которые помогут вам сдать тест на знание финского языка' : 'Proven strategies to help you pass the Finnish language proficiency test'}</p>

      <AdBanner slot="tips-top" />

      <article className="content-article">
        <section className="content-section">
          <h2>{isRu ? '1. Систематически расширяйте словарный запас' : '1. Build Your Vocabulary Systematically'}</h2>
          <p>
            {isRu
              ? 'Словарный запас — это основа изучения языка. Без достаточного количества слов вы не сможете выражать мысли или понимать других. Вот эффективные стратегии для пополнения финского словаря:'
              : 'Vocabulary is the foundation of language learning. Without sufficient words, you cannot express yourself or understand others. Here are effective strategies for building your Finnish vocabulary:'}
          </p>
          <ul>
            <li>
              <strong>{isRu ? 'Используйте интервальное повторение:' : 'Use spaced repetition:'}</strong>{' '}
              {isRu
                ? 'Повторяйте слова через увеличивающиеся промежутки времени. Наша система карточек делает это автоматически. Начните со словаря уровня A1 и постепенно переходите к следующим уровням.'
                : 'Review words at increasing intervals. Our flashcard system helps you do this automatically. Start with A1 vocabulary and progress through each level.'}
            </li>
            <li>
              <strong>{isRu ? 'Учите слова в контексте:' : 'Learn words in context:'}</strong>{' '}
              {isRu
                ? 'Не просто запоминайте переводы. Изучайте примеры предложений и распространённые фразы. Например, не просто учите «talo» (дом), но и «Asun talossa» (Я живу в доме).'
                : 'Don\'t just memorize translations. Learn example sentences and common phrases. For example, don\'t just learn "talo" (house) but also "Asun talossa" (I live in a house).'}
            </li>
            <li>
              <strong>{isRu ? 'Сосредоточьтесь на частотных словах:' : 'Focus on high-frequency words:'}</strong>{' '}
              {isRu
                ? 'Первые 1 000 наиболее распространённых финских слов охватывают около 85% повседневной речи. Наши списки слов уровней A1 и A2 делают упор именно на такие слова.'
                : 'The first 1,000 most common Finnish words cover about 85% of everyday speech. Our A1 and A2 word lists prioritize these high-frequency words.'}
            </li>
            <li>
              <strong>{isRu ? 'Группируйте слова по темам:' : 'Group words by theme:'}</strong>{' '}
              {isRu
                ? 'Учите связанные слова вместе (еда, семья, работа, погода). Это создаёт ассоциативные связи, которые облегчают запоминание.'
                : 'Learn related words together (food, family, work, weather). This creates mental associations that make recall easier.'}
            </li>
            <li>
              <strong>{isRu ? 'Практикуйтесь каждый день:' : 'Practice daily:'}</strong>{' '}
              {isRu
                ? 'Даже 15 минут ежедневной работы со словарём эффективнее, чем 2 часа раз в неделю. Постоянство — залог успеха.'
                : 'Even 15 minutes of daily vocabulary practice is more effective than 2 hours once a week. Consistency is key.'}
            </li>
          </ul>
        </section>

        <section className="content-section">
          <h2>{isRu ? '2. Освойте основы финской грамматики' : '2. Master Finnish Grammar Basics'}</h2>
          <p>
            {isRu
              ? 'Финская грамматика отличается от большинства европейских языков, однако понимание основ существенно ускорит ваше обучение:'
              : 'Finnish grammar is different from most European languages, but understanding the basics will accelerate your learning significantly:'}
          </p>
          <ul>
            <li>
              <strong>{isRu ? 'Падежи (sijat):' : 'Cases (sijat):'}</strong>{' '}
              {isRu
                ? 'В финском языке 15 грамматических падежей. Начните с шести наиболее распространённых: nominatiivi, genetiivi, partitiivi, inessiivi, elatiivi и illatiivi. Они охватывают большинство повседневных ситуаций.'
                : 'Finnish has 15 grammatical cases. Focus first on the most common 6: nominatiivi, genetiivi, partitiivi, inessiivi, elatiivi, and illatiivi. These cover most everyday situations.'}
            </li>
            <li>
              <strong>{isRu ? 'Спряжение глаголов:' : 'Verb conjugation:'}</strong>{' '}
              {isRu
                ? 'Сначала выучите формы настоящего и прошедшего времени. Финские глаголы подчиняются регулярным закономерностям, как только вы освоите типы глаголов (1–6).'
                : 'Learn the present tense and past tense forms first. Finnish verbs follow regular patterns once you know the verb types (1-6).'}
            </li>
            <li>
              <strong>{isRu ? 'Гармония гласных:' : 'Vowel harmony:'}</strong>{' '}
              {isRu
                ? 'В финских словах используются либо задние гласные (a, o, u), либо передние гласные (ä, ö, y). Это влияет на суффиксы и принципиально важно для правильного финского.'
                : 'Finnish words use either front vowels (a, o, u) or back vowels (ae, oe, y). This affects suffixes and is crucial for correct Finnish.'}
            </li>
            <li>
              <strong>{isRu ? 'Порядок слов:' : 'Word order:'}</strong>{' '}
              {isRu
                ? 'Порядок слов в финском относительно свободный (стандартный: подлежащее — сказуемое — дополнение), но изменение порядка меняет акцент. «Minä luen kirjan» и «Kirjan luen minä» оба означают «Я читаю книгу», но с разным ударением.'
                : '"Finnish has relatively free word order (SVO is standard), but emphasis changes meaning. "Minä luen kirjan" vs "Kirjan luen minä" both mean "I read the book" but with different emphasis.'}
            </li>
          </ul>
        </section>

        <AdBanner slot="tips-mid" />

        <section className="content-section">
          <h2>{isRu ? '3. Развивайте навыки аудирования' : '3. Improve Your Listening Skills'}</h2>
          <p>
            {isRu
              ? 'Раздел аудирования теста YKI может быть сложным, поскольку носители финского говорят быстро и используют разговорные выражения. Вот как к нему подготовиться:'
              : 'The listening section of the YKI test can be challenging because native Finnish speakers speak quickly and use colloquial expressions. Here is how to prepare:'}
          </p>
          <ul>
            <li>
              <strong>{isRu ? 'Слушайте Yle Selkouutiset:' : 'Listen to Yle Selkouutiset:'}</strong>{' '}
              {isRu
                ? 'Это финские новости на «простом финском» — идеально для учащихся среднего уровня. Доступно бесплатно в интернете и в виде подкаста.'
                : 'This is Finnish news in "simple Finnish" - perfect for intermediate learners. Available free online and as a podcast.'}
            </li>
            <li>
              <strong>{isRu ? 'Смотрите финское телевидение с субтитрами:' : 'Watch Finnish TV with subtitles:'}</strong>{' '}
              {isRu
                ? 'Начните с субтитрами на финском, затем постепенно переходите к просмотру без них. На Yle Areena есть бесплатный контент на финском.'
                : 'Start with Finnish subtitles, then gradually switch to watching without them. Yle Areena has free Finnish content.'}
            </li>
            <li>
              <strong>{isRu ? 'Используйте наши упражнения по аудированию:' : 'Use our listening practice:'}</strong>{' '}
              {isRu
                ? 'Приложение YKI Testi включает аудиопроизношение для всех слов словаря. Слушайте и повторяйте, чтобы тренировать восприятие на слух.'
                : 'The YKI Testi app includes audio pronunciation for all vocabulary words. Listen and repeat to train your ear.'}
            </li>
            <li>
              <strong>{isRu ? 'Слушайте финские подкасты:' : 'Listen to Finnish podcasts:'}</strong>{' '}
              {isRu
                ? '«Finnish for Foreigners» и «Random Finnish Lesson» — популярные варианты для изучающих язык.'
                : '"Finnish for Foreigners" and "Random Finnish Lesson" are popular choices for learners.'}
            </li>
            <li>
              <strong>{isRu ? 'Практикуйтесь с разными акцентами:' : 'Practice with different accents:'}</strong>{' '}
              {isRu
                ? 'Финский в Хельсинки звучит иначе, чем в Турку или Оулу. Знакомство с разными акцентами помогает на тесте аудирования.'
                : 'Finnish spoken in Helsinki sounds different from Finnish in Turku or Oulu. Exposure to different accents helps with the listening test.'}
            </li>
          </ul>
        </section>

        <section className="content-section">
          <h2>{isRu ? '4. Регулярно практикуйте письмо' : '4. Practice Writing Regularly'}</h2>
          <p>
            {isRu
              ? 'Раздел письма проверяет вашу способность создавать связный финский текст. Разные уровни требуют разных видов письма:'
              : 'The writing section tests your ability to produce coherent Finnish text. Different levels require different types of writing:'}
          </p>
          <div className="tip-cards">
            <div className="tip-card">
              <h4>{isRu ? 'Письмо уровня Perustaso' : 'Perustaso Writing'}</h4>
              <p>{isRu
                ? 'Практикуйте написание коротких сообщений, заполнение анкет и простые описания. Сосредоточьтесь на правильной базовой грамматике и распространённых фразах. Пишите о себе, своём распорядке дня и увлечениях.'
                : 'Practice writing short messages, filling out forms, and simple descriptions. Focus on correct basic grammar and common phrases. Write about yourself, your daily routine, and your hobbies.'}</p>
            </div>
            <div className="tip-card">
              <h4>{isRu ? 'Письмо уровня Keskitaso' : 'Keskitaso Writing'}</h4>
              <p>{isRu
                ? 'Практикуйте написание электронных писем, жалоб и текстов с выражением мнения. Изучите различия между официальным и неофициальным стилем. Практикуйте структурирование текста: вступление, основная часть, заключение.'
                : 'Practice writing emails, complaint letters, and opinion texts. Learn formal and informal register differences. Practice structuring your text with an introduction, body, and conclusion.'}</p>
            </div>
            <div className="tip-card">
              <h4>{isRu ? 'Письмо уровня Ylin taso' : 'Ylin taso Writing'}</h4>
              <p>{isRu
                ? 'Практикуйте написание аргументационных эссе, официальных отчётов и аналитических текстов. Сосредоточьтесь на сложных синтаксических конструкциях, продвинутой лексике и последовательной аргументации.'
                : 'Practice argumentative essays, formal reports, and analytical texts. Focus on complex sentence structures, advanced vocabulary, and coherent argumentation.'}</p>
            </div>
          </div>
          <p>
            <strong>{isRu ? 'Совет профессионала:' : 'Pro tip:'}</strong>{' '}
            {isRu
              ? 'Ведите ежедневный дневник на финском. Пишите хотя бы 3–5 предложений о своём дне. Это развивает беглость письма и помогает выявить пробелы в словарном запасе.'
              : 'Keep a daily Finnish journal. Write even just 3-5 sentences about your day. This builds writing fluency and helps you discover gaps in your vocabulary.'}
          </p>
        </section>

        <section className="content-section">
          <h2>{isRu ? '5. Стратегии для развития разговорной речи' : '5. Speaking Practice Strategies'}</h2>
          <p>
            {isRu
              ? 'Многие учащиеся считают раздел говорения самым волнительным. Вот как обрести уверенность:'
              : 'Many learners find the speaking section the most nerve-wracking. Here is how to build confidence:'}
          </p>
          <ul>
            <li>
              <strong>{isRu ? 'Разговаривайте с собой на финском:' : 'Talk to yourself in Finnish:'}</strong>{' '}
              {isRu
                ? 'Описывайте свои повседневные действия на финском. Это звучит странно, но невероятно эффективно для развития беглости речи.'
                : 'Narrate your daily activities in Finnish. This sounds strange but it is incredibly effective for building fluency.'}
            </li>
            <li>
              <strong>{isRu ? 'Найдите языкового партнёра:' : 'Find a language partner:'}</strong>{' '}
              {isRu
                ? 'Такие сайты, как italki, Tandem и HelloTalk, помогут вам найти носителей финского, которые хотят практиковать ваш язык.'
                : 'Websites like italki, Tandem, and HelloTalk connect you with Finnish speakers who want to practice your language.'}
            </li>
            <li>
              <strong>{isRu ? 'Используйте наши упражнения по говорению:' : 'Use our speaking exercises:'}</strong>{' '}
              {isRu
                ? 'Приложение YKI Testi предоставляет задания для говорения, организованные по темам и уровням, схожие с реальными вопросами теста YKI.'
                : 'The YKI Testi app provides speaking prompts organized by topic and level, similar to actual YKI test questions.'}
            </li>
            <li>
              <strong>{isRu ? 'Записывайте себя:' : 'Record yourself:'}</strong>{' '}
              {isRu
                ? 'Записывайте свою речь на финском и прослушивайте запись. Это помогает замечать ошибки в произношении и области, требующие улучшения.'
                : 'Record yourself speaking Finnish and listen back. This helps you notice pronunciation errors and areas for improvement.'}
            </li>
            <li>
              <strong>{isRu ? 'Отрабатывайте распространённые темы:' : 'Practice common topics:'}</strong>{' '}
              {isRu
                ? 'Раздел говорения YKI часто охватывает темы работы, семьи, хобби, путешествий, здоровья и текущих событий. Подготовьте ответы по этим темам.'
                : 'The YKI speaking test often covers work, family, hobbies, travel, health, and current events. Prepare answers for these topics.'}
            </li>
          </ul>
        </section>

        <section className="content-section">
          <h2>{isRu ? '6. Советы на день экзамена' : '6. Test Day Tips'}</h2>
          <ul>
            <li>
              <strong>{isRu ? 'Хорошо выспитесь:' : 'Sleep well:'}</strong>{' '}
              {isRu
                ? 'Хорошо выспитесь перед экзаменом. Умственная усталость существенно снижает языковые показатели.'
                : 'Get a good night\'s sleep before the test. Mental fatigue significantly impacts language performance.'}
            </li>
            <li>
              <strong>{isRu ? 'Приходите заранее:' : 'Arrive early:'}</strong>{' '}
              {isRu
                ? 'Прибудьте в экзаменационный центр за 15–20 минут до начала, чтобы освоиться.'
                : 'Get to the test center 15-20 minutes before the start time to settle in.'}
            </li>
            <li>
              <strong>{isRu ? 'Внимательно читайте инструкции:' : 'Read instructions carefully:'}</strong>{' '}
              {isRu
                ? 'Инструкции к тесту написаны на финском. Убедитесь, что вы понимаете, что требуется, прежде чем отвечать.'
                : 'Test instructions are in Finnish. Make sure you understand what is being asked before answering.'}
            </li>
            <li>
              <strong>{isRu ? 'Управляйте временем:' : 'Manage your time:'}</strong>{' '}
              {isRu
                ? 'Не задерживайтесь слишком долго на одном вопросе. Если вы застряли, переходите дальше и вернитесь позже.'
                : 'Don\'t spend too long on any single question. If you are stuck, move on and come back later.'}
            </li>
            <li>
              <strong>{isRu ? 'Пишите ответ на каждый вопрос:' : 'Write something for every question:'}</strong>{' '}
              {isRu
                ? 'Даже частичные ответы могут принести баллы. Пустой ответ всегда даёт ноль.'
                : 'Even partial answers can earn points. A blank answer always gets zero.'}
            </li>
            <li>
              <strong>{isRu ? 'Сохраняйте спокойствие:' : 'Stay calm:'}</strong>{' '}
              {isRu
                ? 'Если вы что-то не поняли в разделе аудирования, не паникуйте. Сосредоточьтесь на том, что вы понимаете, и делайте обоснованные догадки.'
                : 'If you don\'t understand something in the listening section, don\'t panic. Focus on what you can understand and make educated guesses.'}
            </li>
          </ul>
        </section>

        <section className="content-section">
          <h2>{isRu ? '7. Рекомендуемый план занятий' : '7. Recommended Study Schedule'}</h2>
          <div className="schedule-table">
            <div className="schedule-row schedule-header">
              <span>{isRu ? 'Время до экзамена' : 'Time Before Test'}</span>
              <span>{isRu ? 'Область подготовки' : 'Focus Area'}</span>
            </div>
            <div className="schedule-row">
              <span>{isRu ? '3–6 месяцев' : '3-6 months'}</span>
              <span>{isRu ? 'Формирование базового словаря (A1–A2), изучение основной грамматики, начало практики аудирования' : 'Build core vocabulary (A1-A2), learn basic grammar, start listening practice'}</span>
            </div>
            <div className="schedule-row">
              <span>{isRu ? '2–3 месяца' : '2-3 months'}</span>
              <span>{isRu ? 'Расширение словаря (B1), ежедневная практика письма, увеличение объёма аудирования' : 'Expand vocabulary (B1), practice writing daily, increase listening exposure'}</span>
            </div>
            <div className="schedule-row">
              <span>{isRu ? '1–2 месяца' : '1-2 months'}</span>
              <span>{isRu ? 'Практика на пробных тестах, работа над слабыми местами, ежедневная практика говорения' : 'Practice with mock tests, focus on weak areas, speaking practice daily'}</span>
            </div>
            <div className="schedule-row">
              <span>{isRu ? 'Последние 2 недели' : 'Last 2 weeks'}</span>
              <span>{isRu ? 'Повторение частотной лексики, практика письма на время, техники расслабления' : 'Review high-frequency vocabulary, practice timed writing, relaxation techniques'}</span>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2>{isRu ? 'Начните практиковаться сегодня' : 'Start Practicing Today'}</h2>
          <p>
            {isRu
              ? 'Лучшее время для начала подготовки к тесту YKI — прямо сейчас. Используйте приложение YKI Testi, чтобы пополнить словарный запас, практиковать письмо и улучшить разговорные навыки — всё бесплатно!'
              : 'The best time to start preparing for the YKI test is now. Use the YKI Testi app to build your vocabulary, practice writing, and improve your speaking skills - all for free!'}
          </p>
          <div className="content-cta-row">
            <Link to="/vocabulary" className="content-cta">{isRu ? 'Начать со словаря' : 'Start with Vocabulary'}</Link>
            <Link to="/yki-guide" className="content-cta content-cta-secondary">{isRu ? 'Читать руководство по YKI' : 'Read YKI Guide'}</Link>
          </div>
        </section>
      </article>

      <AdBanner slot="tips-bottom" />
    </div>
  );
};

export default StudyTipsPage;
